/**
 * useFields Hook
 * 
 * Fetches fields for a collection from the API.
 * Used by permission components to build field selection UI.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, type ApiListResponse } from '@/lib/api';
import { getFieldDisplayName } from '@/utils/get-field-display-name';

// ============== Types ==============

export interface FieldSchema {
    name: string;
    table: string;
    data_type: string;
    default_value: unknown;
    max_length: number | null;
    numeric_precision: number | null;
    numeric_scale: number | null;
    is_nullable: boolean;
    is_unique: boolean;
    is_primary_key: boolean;
    is_generated: boolean;
    has_auto_increment: boolean;
    foreign_key_column: string | null;
    foreign_key_table: string | null;
}

export interface FieldTranslation {
    language: string;
    translation: string;
}

export interface FieldMeta {
    id: number;
    collection: string;
    field: string;
    special: string[] | null;
    interface: string | null;
    options: Record<string, unknown> | null;
    display: string | null;
    display_options: Record<string, unknown> | null;
    readonly: boolean;
    hidden: boolean;
    sort: number | null;
    width: 'half' | 'half-left' | 'half-right' | 'full' | 'fill' | null;
    translations: FieldTranslation[] | null;
    note: string | null;
    conditions: unknown[] | null;
    required: boolean;
    group: string | null;
    validation: Record<string, unknown> | null;
    validation_message: string | null;
}

export interface Field {
    field: string;
    type: string;
    collection: string;
    name?: string; // Display name from translations or formatted field name
    schema: FieldSchema | null;
    meta: FieldMeta | null;
}

interface UseFieldsOptions {
    collection: string | null;
    includeHidden?: boolean;
}

interface UseFieldsResult {
    fields: Field[];
    fieldsFlat: Field[];
    fieldsTree: FieldTree[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
}

export interface FieldTree extends Field {
    children?: FieldTree[];
}

export function useFields({ collection, includeHidden = false }: UseFieldsOptions): UseFieldsResult {
    const [fields, setFields] = useState<Field[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchFields = useCallback(async () => {
        if (!collection) {
            setFields([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get<ApiListResponse<Field>>(`/fields/${collection}`);
            let fetchedFields = response.data || [];

            // Filter hidden fields unless explicitly requested
            if (!includeHidden) {
                fetchedFields = fetchedFields.filter((f: Field) => !f.meta?.hidden);
            }

            // Compute display name from translations or format field name
            const fieldsWithNames = fetchedFields.map((field) => ({
                ...field,
                name: getFieldDisplayName(field),
            }));

            setFields(fieldsWithNames);
        } catch (err) {
            console.error('Error fetching fields:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch fields');
            setFields([]);
        } finally {
            setLoading(false);
        }
    }, [collection, includeHidden]);

    useEffect(() => {
        fetchFields();
    }, [fetchFields]);

    // Build a flat list of all fields
    const fieldsFlat = useMemo(() => {
        return [...fields].sort((a, b) => {
            const sortA = a.meta?.sort ?? 0;
            const sortB = b.meta?.sort ?? 0;
            return sortA - sortB;
        });
    }, [fields]);

    // Build a tree structure for grouped fields
    const fieldsTree = useMemo((): FieldTree[] => {
        const tree: FieldTree[] = [];
        const groupMap = new Map<string, FieldTree>();

        // First pass: identify groups and top-level fields
        for (const field of fields) {
            if (field.meta?.special?.includes('group') || field.type === 'alias') {
                const treeField: FieldTree = { ...field, children: [] };
                groupMap.set(field.field, treeField);
            }
        }

        // Second pass: assign children to groups
        for (const field of fields) {
            const group = field.meta?.group;

            if (group && groupMap.has(group)) {
                groupMap.get(group)!.children!.push({ ...field });
            } else if (!groupMap.has(field.field)) {
                tree.push({ ...field });
            }
        }

        // Add groups to tree
        for (const [, group] of groupMap) {
            tree.push(group);
        }

        // Sort by meta.sort
        tree.sort((a, b) => {
            const sortA = a.meta?.sort ?? 0;
            const sortB = b.meta?.sort ?? 0;
            return sortA - sortB;
        });

        return tree;
    }, [fields]);

    return {
        fields,
        fieldsFlat,
        fieldsTree,
        loading,
        error,
        refresh: fetchFields,
    };
}

/**
 * Get all field names as a flat list
 */
export function getFieldNames(fields: Field[]): string[] {
    return fields.map((f) => f.field);
}

/**
 * Check if a field is a relational field
 */
export function isRelationalField(field: Field): boolean {
    const relationalTypes = ['o2m', 'm2o', 'm2m', 'm2a', 'files', 'translations'];
    return field.meta?.special?.some((s) => relationalTypes.includes(s)) ?? false;
}

/**
 * Check if a field is a system field
 */
export function isSystemField(field: Field): boolean {
    return field.field.startsWith('$') || (field.meta?.special?.includes('no-data') ?? false);
}
